import { isFunction } from '@antv/util';
import { CommonEvent } from '../constants';
import { ELEMENT_TYPES } from '../constants/element';
import { idsOf } from '../utils/id';
import { getElementNthDegreeIds } from '../utils/relation';
import { BaseBehavior } from './base-behavior';
/**
 * <zh/> 悬浮元素交互
 *
 * <en/> Hover element behavior
 * @remarks
 * <zh/> 当鼠标悬停在元素上时，可以激活元素的状态，例如高亮节点或边。
 *
 * <en/> When the mouse hovers over an element, you can activate the state of the element, such as highlighting nodes or edges.
 */
export class HoverActivate extends BaseBehavior {
    static defaultOptions = {
        animation: false,
        enable: true,
        degree: 0,
        state: 'active',
        inactiveState: undefined,
    };
    isFrozen = false;
    constructor(context, options) {
        super(context, Object.assign({}, HoverActivate.defaultOptions, options));
        this.bindEvents();
    }
    toggleFrozen = (e) => {
        this.isFrozen = e.type === 'dragstart';
    };
    bindEvents() {
        const { graph } = this.context;
        this.unbindEvents();
        ELEMENT_TYPES.forEach((type) => {
            graph.on(`${type}:${CommonEvent.POINTER_OVER}`, this.hoverElement);
            graph.on(`${type}:${CommonEvent.POINTER_OUT}`, this.hoverElement);
        });
        const canvas = this.context.canvas.document;
        canvas.addEventListener(`${CommonEvent.DRAG_START}`, this.toggleFrozen);
        canvas.addEventListener(`${CommonEvent.DRAG_END}`, this.toggleFrozen);
    }
    hoverElement = (event) => {
        if (!this.validate(event))
            return;
        const isOver = event.type === CommonEvent.POINTER_OVER;
        this.updateElementsState(event, isOver);
        const { onHover, onHoverEnd } = this.options;
        if (isOver)
            onHover?.(event);
        else
            onHoverEnd?.(event);
    };
    updateElementsState = (event, add) => {
        if (!this.options.state && !this.options.inactiveState)
            return;
        const { graph } = this.context;
        const { state, degree, animation, inactiveState } = this.options;
        const { targetType, target } = event;
        const activeIds = degree
            ? getElementNthDegreeIds(graph, targetType, target.id, degree)
            : [target.id];
        const states = {};
        if (state) {
            Object.assign(states, this.getElementsState(activeIds, state, add));
        }
        if (inactiveState) {
            const inactiveIds = idsOf(graph.getData(), true).filter((id) => !activeIds.includes(id));
            Object.assign(states, this.getElementsState(inactiveIds, inactiveState, add));
        }
        graph.setElementState(states, animation);
    };
    getElementsState = (ids, state, add) => {
        const { graph } = this.context;
        const states = {};
        ids.forEach((id) => {
            const currentState = graph.getElementState(id);
            const updatedState = add ? [...currentState, state] : currentState.filter((s) => s !== state);
            states[id] = updatedState;
        });
        return states;
    };
    validate(event) {
        if (this.destroyed || this.isFrozen)
            return false;
        const { enable } = this.options;
        if (isFunction(enable))
            return enable(event);
        return !!enable;
    }
    unbindEvents() {
        const { graph } = this.context;
        ELEMENT_TYPES.forEach((type) => {
            graph.off(`${type}:${CommonEvent.POINTER_OVER}`, this.hoverElement);
            graph.off(`${type}:${CommonEvent.POINTER_OUT}`, this.hoverElement);
        });
        const canvas = this.context.canvas.document;
        canvas.removeEventListener(`${CommonEvent.DRAG_START}`, this.toggleFrozen);
        canvas.removeEventListener(`${CommonEvent.DRAG_END}`, this.toggleFrozen);
    }
    destroy() {
        this.unbindEvents();
        super.destroy();
    }
}
//# sourceMappingURL=hover-activate.js.map